<template>
  <div class="relative" :style="containerStyle">
    <canvas ref="canvas" />
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
)

/**
 * Read a daisyUI CSS custom property and return a canvas-safe color string.
 * daisyUI v2 stores theme colors as space-separated HSL (e.g. "235 52% 52%").
 * Canvas fillStyle requires `hsl(h, s%, l%)` syntax, so we wrap it.
 */
export function getThemeColor (varName) {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim()

  if (!raw) return null
  if (raw.startsWith('#') || raw.startsWith('rgb')) return raw
  return `hsl(${raw.replace(/ /g, ', ')})`
}

export default {
  name: 'Chart',

  props: {
    type: {
      type: String,
      default: 'bar',
    },
    data: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    height: {
      type: [Number, String],
      default: 300,
    },
  },

  data () {
    return {
      chart: null,
    }
  },

  computed: {
    containerStyle () {
      const h = typeof this.height === 'number' ? `${this.height}px` : this.height
      return { height: h }
    },

    mergedOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        ...this.options,
      }
    },
  },

  watch: {
    data: {
      deep: true,
      handler () {
        this.syncChart()
      },
    },
    options: {
      deep: true,
      handler () {
        this.syncChart()
      },
    },
  },

  mounted () {
    this.createChart()
  },

  beforeDestroy () {
    this.destroyChart()
  },

  methods: {
    createChart () {
      const ctx = this.$refs.canvas.getContext('2d')

      this.chart = new Chart(ctx, {
        type: this.type,
        data: this.data,
        options: this.mergedOptions,
      })
    },

    syncChart () {
      if (!this.chart) return

      this.chart.data = this.data
      this.chart.options = this.mergedOptions
      this.chart.update()
    },

    destroyChart () {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
  },
}
</script>
